import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Package, RotateCcw, Clock } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';

const ShippingAndExchange = () => {
    const navigate = useNavigate();

    const shippingOptions = [
        { icon: Truck, title: 'Standard Shipping', time: '5-7 Business Days', cost: 'Free on orders over \u20B95,000' },
        { icon: Package, title: 'Express Shipping', time: '2-3 Business Days', cost: '\u20B9299' },
        { icon: Clock, title: 'Next Day Delivery', time: '1 Business Day', cost: '\u20B9499 (Metro cities only)' }
    ];

    return (
        <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <Header />

            <section className="bg-[#F8FAF8] py-12 md:py-16">
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
                    <div className="w-12 h-12 md:w-14 md:h-14 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-4">
                        <Truck className="w-6 h-6 text-white" />
                    </div>
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#1A1A1A] mb-2">Shipping & Returns</h1>
                    <p className="text-sm md:text-base text-[#6B7280] max-w-lg mx-auto">Everything you need to know about our shipping options and return policy</p>
                </div>
            </section>

            <section className="py-10 md:py-16">
                <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
                    {/* Shipping Options */}
                    <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-6 md:mb-8 text-center">Shipping Options</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-12 md:mb-16">
                        {shippingOptions.map((option, index) => {
                            const Icon = option.icon;
                            return (
                                <div key={index} className="text-center p-5 border border-[#E5E7EB] hover:border-[#2D6A4F] transition-colors">
                                    <div className="w-10 h-10 bg-[#2D6A4F]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <Icon className="w-5 h-5 text-[#2D6A4F]" />
                                    </div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-1">{option.title}</h3>
                                    <p className="text-xs md:text-sm text-[#6B7280] mb-0.5">{option.time}</p>
                                    <p className="text-xs md:text-sm font-medium text-[#2D6A4F]">{option.cost}</p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Shipping Information */}
                    <div className="space-y-8 md:space-y-10">
                        <div>
                            <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A] mb-4 md:mb-5">Shipping Information</h2>
                            <div className="space-y-5">
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-2">Processing Time</h3>
                                    <p className="text-sm text-[#6B7280] leading-relaxed">All orders are processed within 1-2 business days. Orders placed on weekends or holidays are processed the next business day. Handcrafted items may require an additional 1-2 days for quality checks.</p>
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-2">Order Tracking</h3>
                                    <p className="text-sm text-[#6B7280] leading-relaxed">You will receive a shipping confirmation email with your tracking number once your order ships. Track your order through the link in the email or by logging into your account.</p>
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-2">International Shipping</h3>
                                    <p className="text-sm text-[#6B7280] leading-relaxed">We ship to select countries worldwide. International delivery typically takes 10-15 business days. Customs duties and import taxes may apply and are the responsibility of the buyer.</p>
                                </div>
                            </div>
                        </div>

                        {/* Returns */}
                        <div className="border-t border-[#E5E7EB] pt-8 md:pt-10">
                            <div className="flex items-center gap-3 mb-4 md:mb-5">
                                <div className="w-10 h-10 bg-[#2D6A4F]/10 rounded-full flex items-center justify-center">
                                    <RotateCcw className="w-5 h-5 text-[#2D6A4F]" />
                                </div>
                                <h2 className="text-xl md:text-2xl font-semibold text-[#1A1A1A]">Returns & Exchanges</h2>
                            </div>
                            <div className="space-y-5">
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-2">15-Day Return Policy</h3>
                                    <p className="text-sm text-[#6B7280] leading-relaxed">We accept returns within 15 days of delivery. Items must be unworn, unwashed, and in original condition with all tags attached. For sarees, the fall and pico must be intact. Custom-made items are final sale.</p>
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-2">How to Return</h3>
                                    <ol className="list-decimal list-inside space-y-1.5 text-sm text-[#6B7280] leading-relaxed ml-2">
                                        <li>Log in and go to your order history</li>
                                        <li>Select the order and click "Request Return"</li>
                                        <li>Print the prepaid return label</li>
                                        <li>Package items securely in original packaging</li>
                                        <li>Drop off at any authorized shipping location</li>
                                    </ol>
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-2">Exchanges</h3>
                                    <p className="text-sm text-[#6B7280] leading-relaxed">For size exchanges, contact our team within 15 days of delivery. We recommend placing a new order for the desired item and returning the original for a refund to ensure faster processing.</p>
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-2">Refund Processing</h3>
                                    <p className="text-sm text-[#6B7280] leading-relaxed">Once received, refunds are processed within 5-7 business days to your original payment method. An additional 3-5 business days may be needed for the amount to reflect in your account.</p>
                                </div>
                                <div>
                                    <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A] mb-2">Damaged or Defective Items</h3>
                                    <p className="text-sm text-[#6B7280] leading-relaxed">If you receive a damaged or defective item, contact us immediately at returns@priya.com with your order number and photos. We will arrange a replacement or full refund at no cost to you.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Help */}
                    <div className="mt-10 md:mt-14 p-5 bg-[#F8FAF8]">
                        <h3 className="text-base font-semibold text-[#1A1A1A] mb-2">Need Help?</h3>
                        <p className="text-sm text-[#6B7280] mb-3">Our team is here to assist with any shipping or return questions.</p>
                        <p className="text-sm font-medium text-[#1A1A1A]">Email: support@priya.com</p>
                        <p className="text-sm font-medium text-[#1A1A1A]">Phone: +91 98765 43210 (Mon-Sat, 10am-7pm IST)</p>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ShippingAndExchange;
